import {
    Autocomplete,
    Box,
    Button,
    CircularProgress,
    DialogActions,
    Grid,
    Skeleton,
    TextField,
    Typography,
} from "@mui/material";
import { Controller, useForm, useWatch } from "react-hook-form";
import {
    DYNAMIC_FEATURE_ID_FIELD_NAME,
    PARCELLAIRE_TAB_BOX_STYLE,
    PLOTMAP_DELETE_MAX_FEATURE_TOTAL_SIZE,
    PLOTMAP_SAVE_MAX_FEATURE_TOTAL_SIZE,
} from "./constants";
import { formatDateTime, offset } from "../../utils/formatTime";
import { useCrops, useFetch } from "../../hooks";
import { useMemo, useState } from "react";

import AddCircleIcon from "@mui/icons-material/AddCircle";
import { BACKEND_ROUTES } from "../../backendRoutes";
import { FetchErrorAlert } from "../../components/FetchErrorAlert";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { LinearProgressWithLabel } from "../../components/LinearProgressBar";
import { MODEL_PROPTYPES } from "../../models";
import { PAGINATION } from "../../constants";
import { PARCELLAIRE_REDUCER_ACTIONS } from "./parcellaireReducer";
import PropTypes from "prop-types";
import download from "downloadjs";
import { removeToolId } from "./utils";
import { roundNumber } from "../utils/roundNumber";
import useSWR from "swr";

ParcellaireHCCSaveForm.propTypes = {
    setIsOpen: PropTypes.func.isRequired,
    parcellaireState: PropTypes.object.isRequired,
    parcellaireDispatch: PropTypes.func.isRequired,
    setGeoJsonKey: PropTypes.func.isRequired,
    elapsedSeconds: PropTypes.number.isRequired,
    resetTimer: PropTypes.func.isRequired,
    openSnackbar: PropTypes.func.isRequired,
    initialCompany: PropTypes.shape(MODEL_PROPTYPES.Company),
    initialContract: PropTypes.shape(MODEL_PROPTYPES.Contract),
    initialSite: PropTypes.shape(MODEL_PROPTYPES.Site),
    initialMission: PropTypes.shape(MODEL_PROPTYPES.Mission),
    orthoimageCompany: PropTypes.shape(MODEL_PROPTYPES.Company),
    orthoimageContract: PropTypes.shape(MODEL_PROPTYPES.Contract),
    orthoimageSite: PropTypes.shape(MODEL_PROPTYPES.Site),
    orthoimageMission: PropTypes.shape(MODEL_PROPTYPES.Mission),
};

export function ParcellaireHCCSaveForm({
    setIsOpen,
    parcellaireState,
    parcellaireDispatch,
    setGeoJsonKey,
    elapsedSeconds,
    resetTimer,
    openSnackbar,
    initialCompany,
    initialContract,
    initialSite,
    initialMission,
    orthoimageCompany,
    orthoimageContract,
    orthoimageSite,
    orthoimageMission,
}) {
    const [loading, setLoading] = useState(false);
    const { post, put } = useFetch();

    const { control, setValue, handleSubmit } = useForm({
        defaultValues: {
            company: orthoimageCompany || initialCompany || null,
            contract: orthoimageContract || initialContract || null,
            site: orthoimageSite || initialSite || null,
            mission: orthoimageMission || initialMission || null,
            crop: null,
            progress: 0,
        },
    });

    const [
        companyWatch,
        contractWatch,
        siteWatch,
        missionWatch,
        cropWatch,
        progressWatch,
    ] = useWatch({
        name: ["company", "contract", "site", "mission", "crop", "progress"],
        control,
    });

    const { data: companies, error: companiesFetchError } = useSWR(
        `${BACKEND_ROUTES.COMPANY}?sort=name&limit=${PAGINATION.COMPANY.LIMIT.MAX}`
    );
    const { data: contracts, error: contractsFetchError } = useSWR(
        companyWatch
            ? `${BACKEND_ROUTES.CONTRACT}?sort=name&companyUuid=${companyWatch.uuid}&limit=${PAGINATION.CONTRACT.LIMIT.MAX}`
            : null
    );
    const { data: sites, error: sitesFetchError } = useSWR(
        contractWatch
            ? `${BACKEND_ROUTES.SITE}?sort=name&contractUuid=${contractWatch.uuid}`
            : null
    );
    const { data: missions, error: missionsFetchError } = useSWR(
        siteWatch
            ? `${BACKEND_ROUTES.MISSION}?sort=date&siteUuid=${siteWatch.uuid}`
            : null
    );
    const { data: crops, error: cropsFetchError } = useCrops();

    const missingCrops = useMemo(
        () =>
            Boolean(
                parcellaireState.transformedGeoJson.features.find(
                    (feature) =>
                        !feature.properties?.crop ||
                        !crops.rows.find(
                            (crop) => crop.name === feature.properties.crop
                        )
                )
            ),
        [crops, parcellaireState.transformedGeoJson.features]
    );

    const mergedFetchError =
        contractsFetchError ??
        companiesFetchError ??
        sitesFetchError ??
        missionsFetchError ??
        cropsFetchError;

    if (mergedFetchError) return <FetchErrorAlert error={mergedFetchError} />;

    if (!companies || !crops) {
        return (
            <Skeleton
                animation="wave"
                variant="rectangular"
                width="100%"
                height="25em"
            />
        );
    }

    const onSubmit = async (payload) => {
        parcellaireDispatch({
            type: PARCELLAIRE_REDUCER_ACTIONS.CHECKPOINT,
        });
        setGeoJsonKey(() => new Date().getTime());
        const featuresPayload = parcellaireState.transformedGeoJson.features;

        const plotmapMaxSize = roundNumber(
            featuresPayload.reduce((max, feature) => {
                const featureSize = new Blob([JSON.stringify(feature)]).size;
                if (featureSize >= max) return featureSize;
                return max;
            }, 0)
        );

        const chunkSize = roundNumber(
            PLOTMAP_SAVE_MAX_FEATURE_TOTAL_SIZE / plotmapMaxSize
        );

        const chunkedFeaturesPayload = featuresPayload.reduce(
            (resultArray, item, index) => {
                const chunkIndex = Math.floor(index / chunkSize);

                if (!resultArray[chunkIndex]) {
                    resultArray[chunkIndex] = [];
                }

                const payloadItem = item.properties
                    ? {
                          ...item,
                          properties: {
                              ...item.properties,
                              [DYNAMIC_FEATURE_ID_FIELD_NAME]: undefined,
                          },
                      }
                    : item;

                resultArray[chunkIndex].push(payloadItem);

                return resultArray;
            },
            []
        );

        try {
            setValue("progress", 0);
            setLoading(true);

            let i = 0;

            if (
                payload.mission.uuid === parcellaireState.loadedMissionUuid &&
                parcellaireState.featuresToDelete.length > 0
            ) {
                const chunkedFeaturesToDeletePayload =
                    parcellaireState.featuresToDelete.reduce(
                        (batches, uuid, index) => {
                            if (
                                index %
                                    PLOTMAP_DELETE_MAX_FEATURE_TOTAL_SIZE ===
                                0
                            )
                                batches.push([]);
                            batches.at(-1).push(uuid);
                            return batches;
                        },
                        []
                    );
                for (const chunk of chunkedFeaturesToDeletePayload) {
                    await post(
                        `${BACKEND_ROUTES.MISSION}/${payload.mission.uuid}/bulk-delete-mission-features?deleteParent=true`,
                        {
                            body: {
                                missionFeatureUuids: chunk,
                            },
                            forwardError: true,
                        }
                    );
                    i += chunk.length;
                    setValue(
                        "progress",
                        (i * 100) /
                            (featuresPayload.length +
                                parcellaireState.featuresToDelete.length)
                    );
                }
                parcellaireDispatch({
                    type: PARCELLAIRE_REDUCER_ACTIONS.CLEAR_DELETE,
                });
            }

            for (const chunk of chunkedFeaturesPayload) {
                await put(
                    `${BACKEND_ROUTES.MISSION}/${payload.mission.uuid}/plot-map`,
                    {
                        body: {
                            features: chunk,
                            cropUuid: payload.crop?.uuid ?? undefined,
                            timeSpentEditing:
                                (missionWatch?.timeSpentEditing ?? 0) +
                                elapsedSeconds,
                        },
                        forwardError: true,
                    }
                );
                i += chunk.length;
                setValue(
                    "progress",
                    (i * 100) /
                        (featuresPayload.length +
                            parcellaireState.featuresToDelete.length)
                );
            }
            if (payload.export) onExport();
            featuresPayload.map((feature, index) => {
                if (feature.properties)
                    feature.properties[DYNAMIC_FEATURE_ID_FIELD_NAME] = index;
                return feature;
            });
            // TRANSFORM_TOOL_ID values were just rewritten above, so any
            // scaleMap entries keyed by the old ids are now stale.
            parcellaireDispatch({
                type: PARCELLAIRE_REDUCER_ACTIONS.CLEAR_SCALE_MAP,
            });
            resetTimer();
            setLoading(false);
            setIsOpen(false);
            openSnackbar("Parcellaire saved successfully.", "success");
        } catch (error) {
            setLoading(false);
            openSnackbar(
                "Something went wrong. Please try again later.",
                "error"
            );
        }
    };

    const onExport = async () => {
        download(
            JSON.stringify(removeToolId(parcellaireState.transformedGeoJson)),
            "plotmap.geojson"
        );
    };

    const onSaveAndExport = async (payload) => {
        await onSubmit({ ...payload, export: true });
    };

    return (
        <form>
            <Box sx={PARCELLAIRE_TAB_BOX_STYLE}>
                <Grid container spacing={3} alignItems="center">
                    <Grid size={12}>
                        <Controller
                            control={control}
                            name="company"
                            render={({
                                field: { ref, onChange, ...fieldProps },
                            }) => (
                                <Autocomplete
                                    {...fieldProps}
                                    id="company"
                                    options={companies.rows}
                                    isOptionEqualToValue={(option, value) =>
                                        option.uuid === value.uuid
                                    }
                                    getOptionLabel={(option) => option.name}
                                    onChange={(_, value) => {
                                        onChange(value);
                                        if (
                                            value &&
                                            contractWatch?.companyUuid !==
                                                value.uuid
                                        ) {
                                            setValue("contract", null);
                                            setValue("site", null);
                                            setValue("mission", null);
                                        }
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            inputRef={ref}
                                            label="Company"
                                        />
                                    )}
                                />
                            )}
                        />
                    </Grid>
                    <Grid size={12}>
                        <Controller
                            control={control}
                            name="contract"
                            render={({
                                field: { ref, onChange, ...fieldProps },
                            }) => (
                                <Autocomplete
                                    {...fieldProps}
                                    id="contract"
                                    options={
                                        companyWatch
                                            ? (contracts?.rows ?? [])
                                            : []
                                    }
                                    isOptionEqualToValue={(option, value) =>
                                        option.uuid === value.uuid
                                    }
                                    getOptionLabel={(option) => option.name}
                                    onChange={(_, value) => {
                                        onChange(value);
                                        if (
                                            siteWatch?.contractUuid !==
                                            value?.uuid
                                        ) {
                                            setValue("site", null);
                                            setValue("mission", null);
                                        }
                                    }}
                                    disabled={!companyWatch}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            inputRef={ref}
                                            label="Campaign"
                                        />
                                    )}
                                />
                            )}
                        />
                    </Grid>
                    <Grid size={12}>
                        <Controller
                            control={control}
                            name="site"
                            render={({
                                field: { ref, onChange, ...fieldProps },
                            }) => (
                                <Autocomplete
                                    {...fieldProps}
                                    id="site"
                                    options={
                                        contractWatch ? (sites?.rows ?? []) : []
                                    }
                                    isOptionEqualToValue={(option, value) =>
                                        option.uuid === value.uuid
                                    }
                                    getOptionLabel={(option) => option.name}
                                    onChange={(_, value) => {
                                        onChange(value);
                                        if (
                                            missionWatch?.siteUuid !==
                                            value?.uuid
                                        ) {
                                            setValue("mission", null);
                                        }
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            inputRef={ref}
                                            label="Site"
                                        />
                                    )}
                                    disabled={!contractWatch || !sites}
                                />
                            )}
                        />
                    </Grid>
                    <Grid size={12}>
                        <Controller
                            control={control}
                            name="mission"
                            render={({
                                field: { ref, onChange, ...fieldProps },
                            }) => (
                                <Autocomplete
                                    {...fieldProps}
                                    id="mission"
                                    options={
                                        siteWatch ? (missions?.rows ?? []) : []
                                    }
                                    isOptionEqualToValue={(option, value) =>
                                        option.uuid === value.uuid
                                    }
                                    getOptionLabel={(option) =>
                                        formatDateTime(
                                            offset(new Date(option.date), true)
                                        )
                                    }
                                    onChange={(_, value) => {
                                        onChange(value);
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            inputRef={ref}
                                            label="Mission"
                                        />
                                    )}
                                    disabled={!siteWatch || !missions}
                                />
                            )}
                        />
                    </Grid>
                    {missingCrops && (
                        <>
                            <Grid size={12}>
                                <Typography color="error">
                                    Some features are missing their crop. Please
                                    select a crop to fall back to when
                                    necessary.
                                </Typography>
                            </Grid>
                            <Grid size={12}>
                                <Controller
                                    control={control}
                                    name="crop"
                                    render={({
                                        field: { ref, onChange, ...fieldProps },
                                    }) => (
                                        <Autocomplete
                                            {...fieldProps}
                                            id="crop"
                                            options={crops.rows}
                                            isOptionEqualToValue={(
                                                option,
                                                value
                                            ) => option.uuid === value.uuid}
                                            getOptionLabel={(option) =>
                                                option.name
                                            }
                                            onChange={(_, value) => {
                                                onChange(value);
                                            }}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    inputRef={ref}
                                                    label="Crop"
                                                />
                                            )}
                                        />
                                    )}
                                />
                            </Grid>
                        </>
                    )}
                    {loading && (
                        <Grid size={12}>
                            <LinearProgressWithLabel value={progressWatch} />
                        </Grid>
                    )}
                </Grid>
                <DialogActions>
                    <Button type="button" onClick={() => setIsOpen(false)}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={
                            loading ? (
                                <CircularProgress size="1em" />
                            ) : (
                                <FileDownloadIcon />
                            )
                        }
                        type="submit"
                        disabled={loading}
                        onClick={handleSubmit(onExport)}
                    >
                        Export
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={
                            loading ? (
                                <CircularProgress size="1em" />
                            ) : (
                                <AddCircleIcon />
                            )
                        }
                        type="submit"
                        disabled={
                            loading ||
                            !missionWatch ||
                            (missingCrops && !cropWatch)
                        }
                        onClick={handleSubmit(onSubmit)}
                    >
                        Save
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={
                            loading ? (
                                <CircularProgress size="1em" />
                            ) : (
                                <FileDownloadIcon />
                            )
                        }
                        type="submit"
                        disabled={
                            loading ||
                            !missionWatch ||
                            (missingCrops && !cropWatch)
                        }
                        onClick={handleSubmit(onSaveAndExport)}
                    >
                        Save & Export
                    </Button>
                </DialogActions>
            </Box>
        </form>
    );
}
